import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Paper,
  Chip,
  Stack,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  HelpOutline as HelpIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Logo from '../components/Logo';
import ChatbotWidget from '../components/ChatbotWidget';

const FAQ: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqCategories = [
    {
      category: 'Getting Started',
      color: '#10B981',
      questions: [
        {
          question: 'Which social media accounts can I connect?',
          answer: 'You can connect your Instagram and Facebook pages to manage conversations from a single panel. We support Instagram Business accounts and Facebook Pages. More platforms like TikTok and WhatsApp may be added in future updates.',
        },
        {
          question: 'How long does setup take?',
          answer: 'Connecting your account typically takes about 30 seconds. Once connected, your messages appear in one panel and you can enable automated replies immediately. The process is simple: select your platform, authorize the connection, and you\'re ready to go.',
        },
        {
          question: 'Do I need technical knowledge to use Social In One?',
          answer: 'No technical knowledge is required. Our platform is designed to be user-friendly and intuitive. You can set up automation, create prompts, and manage messages with just a few clicks.',
        },
      ],
    },
    {
      category: 'Features & Capabilities',
      color: '#6366F1',
      questions: [
        {
          question: 'How do AI responses work?',
          answer: 'Messages are analyzed based on your custom prompts and answered with short, natural replies tailored to the context. Our AI uses advanced language models to understand intent and generate appropriate responses. You can always review and override AI responses with manual replies.',
        },
        {
          question: 'Can I customize prompts?',
          answer: 'Yes. You can create unlimited custom prompts, toggle them on/off, and test them in a realistic scenario with Prompt Tester. You can also generate prompts by uploading an image of your product or service information, and the AI will extract relevant details to create a tailored prompt.',
        },
        {
          question: 'What is the Prompt Tester?',
          answer: 'Prompt Tester is a simulation tool that lets you test how your AI agent will respond to different customer messages before going live. This helps you refine your prompts and ensure quality responses.',
        },
        {
          question: 'Can I switch between manual and automated responses?',
          answer: 'Absolutely. You have full control. You can enable/disable automation at any time, respond manually to specific messages, or use a hybrid approach where AI handles routine questions and you handle complex queries.',
        },
        {
          question: 'Does the platform support multiple languages?',
          answer: 'Yes, especially in our Professional and Enterprise plans. The AI can detect and respond in multiple languages, making it ideal for businesses with international audiences.',
        },
      ],
    },
    {
      category: 'Pricing & Plans',
      color: '#E1306C',
      questions: [
        {
          question: 'How should I choose between plans?',
          answer: 'Starter ($4.99/month) is ideal for small-scale usage with up to 500 messages per month and 1 social media account. Professional ($9.99/month) is great if you handle higher message volumes (up to 5,000/month) and need up to 3 accounts with advanced features. For unlimited scale, custom integrations, and dedicated support, choose Enterprise ($49.99/month).',
        },
        {
          question: 'Can I upgrade or downgrade my plan?',
          answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades will take effect at the start of your next billing cycle.',
        },
        {
          question: 'Is there a free trial?',
          answer: 'We offer a trial period for new users to test the platform. Contact our support team for trial access details.',
        },
        {
          question: 'What happens if I exceed my message limit?',
          answer: 'On Starter and Professional plans, you\'ll receive a notification when approaching your limit. You can upgrade to a higher plan or purchase additional message credits. Enterprise plan offers unlimited messages.',
        },
      ],
    },
    {
      category: 'Security & Privacy',
      color: '#10A37F',
      questions: [
        {
          question: 'Is my data secure?',
          answer: 'Yes. We use enterprise-grade encryption and security measures to protect your data. All messages and customer information are stored securely, and we comply with industry-standard privacy regulations.',
        },
        {
          question: 'Who has access to my messages?',
          answer: 'Only you and authorized users on your account have access to your messages. Our AI processes messages to generate responses, but human staff never access your data unless you explicitly request support.',
        },
        {
          question: 'Can I delete my data?',
          answer: 'Yes, you have full control over your data. You can delete messages, prompts, or your entire account at any time from the settings page.',
        },
      ],
    },
    {
      category: 'Integrations & Advanced',
      color: '#FF6B35',
      questions: [
        {
          question: 'Do you offer API access?',
          answer: 'API access is available on the Enterprise plan. This allows you to integrate Social In One with your existing tools, CRM systems, or custom applications.',
        },
        {
          question: 'Can I integrate with my CRM or other tools?',
          answer: 'Yes, Enterprise plan includes custom integrations. We can work with you to connect Social In One to your existing business tools and workflows.',
        },
        {
          question: 'Is white-label available?',
          answer: 'White-label options are available on the Enterprise plan, allowing you to rebrand the platform with your own logo and colors.',
        },
      ],
    },
  ];

  return (<>
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(99, 102, 241, 0.08))' }}>
      {/* Header */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Logo height={48} />
            </Box>

            {/* Navigation */}
            <Stack direction="row" spacing={3} alignItems="center">
              <Button
                variant="text"
                onClick={() => navigate('/')}
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    background: 'rgba(16, 185, 129, 0.05)',
                  },
                }}
              >
                Home
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                  color: '#10B981',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    borderColor: '#10B981',
                    background: 'rgba(16, 185, 129, 0.05)',
                  },
                }}
              >
                Sign In
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Page Title Section */}
      <Box
        sx={{
          pt: 14,
          pb: 0,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
              }}
            >
              <HelpIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Everything you need to know about Social In One
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {faqCategories.map((category, catIndex) => (
          <motion.div
            key={catIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: catIndex * 0.1 }}
          >
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Chip
                  label={category.category}
                  sx={{
                    background: `${category.color}15`,
                    color: category.color,
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 1,
                  }}
                />
                <Box sx={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.06)' }} />
              </Box>

              {category.questions.map((item, qIndex) => (
                <Accordion
                  key={qIndex}
                  disableGutters
                  sx={{
                    background: 'white',
                    borderRadius: 2,
                    mb: 2,
                    border: '1px solid rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    '&:before': { display: 'none' },
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: `${category.color}30`,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: category.color }} />}
                    sx={{
                      px: 3,
                      py: 1.5,
                      '&:hover': { background: `${category.color}05` },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, py: 2, background: 'rgba(0,0,0,0.01)' }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.8 }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </motion.div>
        ))}

        {/* Contact CTA */}
        <Paper
          elevation={0}
          sx={{
            mt: 6,
            p: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(99, 102, 241, 0.08))',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Still have questions?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Our support team is here to help you get the most out of Social In One.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component="a"
            href="mailto:info@socialinone.ai?subject=Support Request"
            sx={{
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
              textDecoration: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669, #047857)',
                boxShadow: '0 12px 20px rgba(16, 185, 129, 0.3)',
                textDecoration: 'none',
              },
            }}
          >
            Contact Support
          </Button>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        id="footer"
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
          color: 'white',
          mt: 12,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, 
            gap: 4,
            justifyContent: { xs: 'center', md: 'stretch' }
          }}>
            {/* Company Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Logo height={32} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 3,
                    maxWidth: { xs: '100%', md: '64%' },
                  }}
                >
                  Transform your social media presence with AI-powered automation. 
                  Engage with your audience 24/7 and grow your brand effortlessly.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                  <IconButton
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#1877F2' },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/socialinone/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#E1306C' },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.tiktok.com/@socialinone"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00f2ea' },
                    }}
                  >
                    <Box
                      component="svg"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      sx={{ width: 24, height: 24, fill: 'currentColor' }}
                    >
                      <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z"/>
                    </Box>
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.youtube.com/@SocialinOne"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#FF0000' },
                    }}
                  >
                    <YouTubeIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            {/* Support */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Support
              </Typography>
              <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  FAQ
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  component={Link}
                  to="/help-center"
                  variant="text"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Help Center
                </Button>
              </Stack>
            </Box>

            {/* Contact Info */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    info@socialinone.ai
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <LocationIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Almere, Netherlands
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'center' },
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              © 2025 NowUp. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 3 },
                ml: { xs: 0, md: 0 },
              }}
            >
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/terms-of-use')}
              >
                Terms of Service
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/cookie-policy')}
              >
                Cookie Policy
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
    <ChatbotWidget />
  </>);
};

export default FAQ;

